import type { JSONContent } from "@tiptap/react";
import type { SubTemplateData } from "@/types/evaluation";
import { nodeTextContent, stripMarkdown, renderField } from "@/components/unified/renderField";
import { parseFieldDef } from "@/lib/parseFieldDef";

/**
 * Returns reportLineMap[tiptapChildIdx] = flat DOM line index of the first
 * .report-line element that child produces, mirroring documentToReportSections
 * including trailing-empty-line trimming and empty-section filtering.
 */

interface TrackedLine {
  nodeIdx: number;
  text: string;
}

interface TrackedSection {
  lines: TrackedLine[];
}

interface LineBuildState {
  current: TrackedSection;
  readonly sections: TrackedSection[];
  currentSectionVisible: boolean;
  afterHidden: boolean;
}

function handleLineSection(
  node: JSONContent,
  state: LineBuildState,
  sectionToggles: Record<string, boolean>,
): void {
  if (state.current.lines.length > 0) {
    state.sections.push(state.current);
  }
  const sectionId = node.attrs?.sectionId as string | undefined;
  state.currentSectionVisible = sectionId ? (sectionToggles[sectionId] ?? true) : true;
  state.current = { lines: [] };
  state.afterHidden = true;
}

function handleLineHeading(node: JSONContent, i: number, state: LineBuildState): void {
  if (!state.currentSectionVisible) {
    return;
  }
  const text = nodeTextContent(node);
  if (text) {
    state.current.lines.push({ nodeIdx: i, text });
  }
  state.afterHidden = false;
}

function handleLineParagraph(
  node: JSONContent,
  i: number,
  state: LineBuildState,
  interpolate: (s: string) => string,
): void {
  if (!state.currentSectionVisible) {
    return;
  }
  const text = nodeTextContent(node);
  if (!text && state.afterHidden) {
    state.afterHidden = false;
    return;
  }
  state.current.lines.push({ nodeIdx: i, text: text ? interpolate(stripMarkdown(text)) : "" });
  state.afterHidden = false;
}

function handleLineFieldNode(
  node: JSONContent,
  i: number,
  state: LineBuildState,
  values: Record<string, unknown>,
  subTemplateValues: Record<string, SubTemplateData>,
  interpolate: (s: string) => string,
  fieldToggles: Record<string, boolean>,
): void {
  if (!state.currentSectionVisible) {
    return;
  }
  const field = parseFieldDef((node.attrs?.fieldDef as string | undefined) ?? "");
  if (!field) {
    return;
  }
  if (fieldToggles[field.id] === false) {
    state.afterHidden = true;
    return;
  }
  const rendered = renderField(field, values[field.id], subTemplateValues[field.id], interpolate);
  if (rendered) {
    for (const fl of rendered.split("\n")) {
      state.current.lines.push({ nodeIdx: i, text: fl });
    }
  }
  state.afterHidden = false;
}

function computeFlatLineIndices(sections: TrackedSection[], result: number[]): number {
  let flatLine = 0;
  for (const s of sections) {
    if (s.lines.length === 0) {
      continue;
    }
    for (const line of s.lines) {
      if (result[line.nodeIdx] === -1) {
        result[line.nodeIdx] = flatLine;
      }
      flatLine++;
    }
  }
  return flatLine;
}

function fillUnmappedNodes(result: number[], flatLine: number): void {
  // Fill unmapped nodes: prefer the NEXT known line (so section headings
  // map to the start of their section's content, not the end of the previous).
  // Fall back to previous known line for trailing unmapped nodes.
  let next = flatLine > 0 ? flatLine - 1 : 0;
  for (let i = result.length - 1; i >= 0; i--) {
    const val = result[i];
    if (val === undefined) {
      continue;
    }
    if (val === -1) {
      result[i] = next;
    } else {
      next = val;
    }
  }
}

export function buildReportLineMap(
  doc: JSONContent,
  values: Record<string, unknown>,
  subTemplateValues: Record<string, SubTemplateData>,
  interpolate: (s: string) => string,
  sectionToggles: Record<string, boolean> = {},
  fieldToggles: Record<string, boolean> = {},
): number[] {
  const nodes = doc.content ?? [];
  const result: number[] = Array.from<number>({ length: nodes.length }).fill(-1);

  // Build sections with node-index tracking, mirroring documentToReportSections.
  // Line counts must match the actual report output (which renders ALL content
  // regardless of collapse state).
  const state: LineBuildState = {
    afterHidden: false,
    current: { lines: [] },
    currentSectionVisible: true,
    sections: [],
  };

  for (const [i, node] of nodes.entries()) {
    switch (node.type) {
      case "section": {
        handleLineSection(node, state, sectionToggles);
        break;
      }
      case "heading": {
        handleLineHeading(node, i, state);
        break;
      }
      case "scoregroupNode": {
        state.afterHidden = true;
        break;
      }
      case "paragraph": {
        handleLineParagraph(node, i, state, interpolate);
        break;
      }
      case "fieldNode": {
        handleLineFieldNode(node, i, state, values, subTemplateValues, interpolate, fieldToggles);
        break;
      }
      // No default
    }
  }
  if (state.current.lines.length > 0) {
    state.sections.push(state.current);
  }

  const flatLine = computeFlatLineIndices(state.sections, result);
  fillUnmappedNodes(result, flatLine);
  return result;
}
